import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Tooltip,
  IconButton,
  alpha,
  Collapse
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Bookmark as BookmarkIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useBooking } from '../../contexts/BookingContext';

// Define consistent style variables
const COLORS = {
  primary: '#0070F3',
  text: {
    primary: '#1F2937',
    secondary: '#4B5563',
    muted: '#94a3b8',
    accent: '#0070F3'
  },
  bg: {
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 112, 243, 0.05)',
    mainOption: 'rgba(0, 112, 243, 0.08)'
  },
  border: 'rgba(0, 0, 0, 0.08)'
};

const FILTER_CONFIGURATIONS = {
  // All Bookings (no status filter)
  'all': {
    filters: {},
    label: 'All Bookings',
    type: 'main',
    status: 'all'
  },
  
  // Draft Status
  'draft': {
    filters: { status: 'draft' },
    label: 'Draft',
    type: 'main',
    status: 'draft'
  },
  
  // Pending Status
  'pending': {
    filters: { status: 'pending' },
    label: 'Pending Approval',
    type: 'main',
    status: 'pending'
  },
  // Reserved Status
  'reserved': {
    filters: { status: 'reserved' },
    label: 'Reserved',
    type: 'main',
    status: 'reserved'
  },
  
  // Confirmed Status
  'confirmed': {
    filters: { status: 'confirmed' },
    label: 'Confirmed',
    type: 'main',
    status: 'confirmed'
  },
  // Completed Status
  'completed': {
    filters: { status: 'completed' },
    label: 'Completed',
    type: 'main',
    status: 'completed'
  },
  
  // Cancelled Status
  'cancelled': {
    filters: { status: 'cancelled' },
    label: 'Cancelled',
    type: 'main',
    status: 'cancelled'
  },
  // Expired Status
  'expired': {
    filters: { status: 'expired' },
    label: 'Expired',
    type: 'main',
    status: 'expired'
  }
};

// Function to check if a booking matches filter criteria
const matchesFilterCriteria = (booking, criteria) => {
  return Object.entries(criteria).every(([key, value]) => {
    // Handle array values in criteria
    if (Array.isArray(value)) {
      if (Array.isArray(booking[key])) {
        return booking[key].some(item => value.includes(item));
      }
      return value.includes(booking[key]);
    }
    if (key === 'payment_status' && typeof value === 'object') {
      const min_value = value.min_value || 0;
      const max_value = value.max_value || Infinity;
      const paymentStatus = booking.payment_status || 0;
      return paymentStatus >= min_value && paymentStatus <= max_value;
    }
    
    // For simple fields, just compare values
    return booking[key] === value;
  });
};

const BookingStatusSidebar = ({ width = 200, collapsed = false, onToggleCollapse, statusCounts, dateRangeState, selectedStatus = 'all', onStatusChange }) => {
  const { filters: appliedFilters, setFilters, fetchBookings } = useBooking();
  const [expandedSection, setExpandedSection] = useState(null);

  const handleToggleSection = (sectionId) => {
    setExpandedSection(prev => (prev === sectionId ? null : sectionId));
  };

  useEffect(() => {
    if (selectedStatus && selectedStatus.includes('_')) {
      const parentId = selectedStatus.split('_')[0];
      if (parentId !== expandedSection) {
        setExpandedSection(parentId);
      }
    }
  }, [selectedStatus]);

  const bookingSections = useMemo(() => {
    const stageOrder = Object.keys(FILTER_CONFIGURATIONS);
    const sections = stageOrder.map(statusKey => {
      const statusData = statusCounts?.[statusKey];
      const filterConfig = FILTER_CONFIGURATIONS[statusKey];
      
      // Always create section from FILTER_CONFIGURATIONS, use statusCounts data if available
      const children = [];
      
      // Check if this status has sub-filters in FILTER_CONFIGURATIONS
      if (filterConfig.type === 'sub' && filterConfig.parent) {
        // This is a sub-item, skip it as it will be handled by parent
        return null;
      }
      
      // Find all sub-items for this status
      const subItems = Object.entries(FILTER_CONFIGURATIONS).filter(([key, config]) => 
        config.type === 'sub' && config.parent === statusKey
      );
      
      subItems.forEach(([subKey, subConfig]) => {
        const subData = statusData?.filters?.[subKey.replace(`${statusKey}_`, '')];
        const count = Number(subData?.count ?? 0);
        children.push({
          id: subKey,
          label: subConfig.label,
          count,
        });
      });
      
      return {
        id: statusKey,
        label: filterConfig.label,
        count: Number(statusData?.count ?? 0),
        children,
        icon: filterConfig.icon
      }
    }).filter(Boolean);

    return sections;
  }, [statusCounts]);

  const handleStatusFilter = async (statusId) => {
    const filterConfig = FILTER_CONFIGURATIONS[statusId];
    
    // Update the selected status via callback
    if (onStatusChange) {
      onStatusChange(statusId);
    }
    
    if (filterConfig && filterConfig.filters) {
      // Apply the filters through context
      setFilters(filterConfig.filters);
      // Trigger fetch with new filters
      await fetchBookings(filterConfig.filters, dateRangeState, null, true);
    } else {
      // Clear filters for 'all' bookings
      setFilters({});
      await fetchBookings({}, dateRangeState, null, true);
    }
  };

  // Common styles for toggle button
  const toggleButtonStyles = {
    position: 'absolute',
    top: '10px',
    right: '-12px',
    width: '24px',
    height: '24px',
    backgroundColor: '#fff',
    border: '1px solid',
    borderColor: 'rgba(0,0,0,0.1)',
    borderRadius: '50%',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
    zIndex: 10,
    padding: 0,
    '&:hover': {
      backgroundColor: '#f8f9fa',
    }
  };

  if (!statusCounts) {
    return <div>Loading...</div>;
  }

  // Render collapsed sidebar
  if (collapsed) {
    return (
      <Box 
        sx={{ 
          width: '10px', 
          borderRight: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          height: '100%',
          bgcolor: '#f8fafc'
        }}
      >
        <Tooltip title="Expand sidebar" placement="right">
          <IconButton 
            size="small" 
            onClick={onToggleCollapse}
            sx={toggleButtonStyles}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // Render expanded sidebar
  return (
    <Box
      sx={{
        width: width,
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
        transition: 'width 0.2s ease, box-shadow 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        height: '100vh',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.03)'
      }}
    >
      <Tooltip title="Collapse sidebar" placement="right">
        <IconButton 
          size="small" 
          onClick={onToggleCollapse}
          sx={toggleButtonStyles}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box sx={{ 
        marginTop: '10px',
        overflow: 'auto', 
        flexGrow: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.15)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        }
      }}>
        <List 
          sx={{ 
            py: 0.5, 
            mt: 0,
            '& .MuiListItemButton-root': {
              borderRadius: '4px',
              mx: 0.75,
              transition: 'all 0.2s ease',
            }
          }}
        >
          {bookingSections.map((section) => {
            const isParentSelected = selectedStatus === section.id;
            const hasChildren = section.children.length > 0;
            const isExpanded = expandedSection === section.id;

            return (
              <React.Fragment key={section.id}>
                <ListItemButton
                  selected={isParentSelected && !hasChildren}
                  onClick={() => hasChildren ? handleToggleSection(section.id) : handleStatusFilter(section.id)}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    position: 'relative',
                    minHeight: 36,
                    borderRadius: '4px',
                    mb: 0.25,
                    fontWeight: 500,
                    backgroundColor: isParentSelected && !hasChildren ? COLORS.bg.mainOption : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: COLORS.bg.mainOption,
                      '&:hover': {
                        bgcolor: alpha(COLORS.primary, 0.15),
                      },
                    },
                    '&:hover': {
                      bgcolor: alpha(COLORS.primary, 0.08),
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      section.icon === 'delete' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DeleteIcon fontSize="small" />
                          <span>{section.label}</span>
                        </Box>
                      ) : (
                        section.label
                      )
                    }
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: isParentSelected ? 600 : 500,
                      color: isParentSelected ? COLORS.text.accent : COLORS.text.primary,
                      fontSize: '0.875rem',
                      noWrap: true,
                    }}
                    sx={{ m: 0 }}
                  />
                  {hasChildren && (
                    isExpanded ? 
                    <ExpandLessIcon fontSize="small" sx={{ color: '#777', ml: 1 }} /> : 
                    <ExpandMoreIcon fontSize="small" sx={{ color: '#777', ml: 1 }} />
                  )}
                  <Typography 
                    variant="caption"
                    sx={{ 
                      ml: 0.5,
                      minWidth: '22px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '10px',
                      backgroundColor: isParentSelected ? alpha(COLORS.primary, 0.15) : 'transparent',
                      color: isParentSelected ? COLORS.text.accent : COLORS.text.primary,
                      fontWeight: isParentSelected ? 600 : 500,
                      padding: '0 4px',
                      fontSize: '0.75rem',
                    }}
                  >
                    {section.count ?? 0}
                  </Typography>
                </ListItemButton>

                {hasChildren && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 1, pr: 0 }}>
                      {section.children.map((child) => {
                        const isChildSelected = selectedStatus === child.id;
                        return (
                          <ListItemButton
                            key={child.id}
                            selected={isChildSelected}
                            onClick={() => handleStatusFilter(child.id)}
                            sx={{
                              py: 0.5,
                              pl: 1.5, 
                              pr: 1.5,
                              position: 'relative',
                              minHeight: 28,
                              borderRadius: '4px',
                              mb: 0.25,
                              '&.Mui-selected': {
                                bgcolor: alpha(COLORS.bg.selected, 0.5),
                                '&:hover': {
                                  bgcolor: alpha(COLORS.bg.selected, 0.7),
                                },
                              },
                              '&:hover': {
                                bgcolor: COLORS.bg.hover,
                              },
                            }}
                          >
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                variant: 'body2',
                                fontWeight: isChildSelected ? 500 : 400,
                                color: isChildSelected ? COLORS.text.accent : COLORS.text.secondary,
                                fontSize: '0.8125rem',
                                noWrap: true,
                              }}
                              sx={{ m: 0 }}
                            />
                            <Typography 
                              variant="caption"
                              sx={{ 
                                ml: 0.5,
                                minWidth: '22px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '10px',
                                backgroundColor: isChildSelected ? alpha(COLORS.primary, 0.08) : 'transparent',
                                color: isChildSelected ? COLORS.text.accent : COLORS.text.secondary,
                                fontWeight: isChildSelected ? 500 : 400,
                                padding: '0 4px',
                                fontSize: '0.75rem',
                              }}
                            >
                              {child.count ?? 0}
                            </Typography>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>

        <Box sx={{ flexGrow: 1, minHeight: '100px' }} />
      </Box>
    </Box>
  );
};

export default BookingStatusSidebar;